import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import config from '../../config/config.json';
import axios from 'axios';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  section: {
    marginBottom: '20px',
  },
  teamCard: {
    padding: '15px',
    borderRadius: '6px',
    marginTop: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  teamLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '4px',
    marginRight: '15px',
  },
  centeredContent: {
    textAlign: 'center',
  },
  centeredHeader: {
    textAlign: 'center',
    margin: 0,
  },
}));

type RulesetDetailPageParams = Record<'id', string>;

interface Team {
  id: number;
  name: string;
  slug: string;
  logo_url?: string;
}

interface Ruleset {
  id: number;
  name: string;
  description: string;
  published: boolean;
  created_at: string;
  updated_at: string;
  team: Team;
}

export function RulesetDetailPage() {
  const { id } = useParams<RulesetDetailPageParams>();
  const { t } = useTranslation();
  const classes = useStyles();

  useDocumentTitle(t('core.rulesetDetails'));

  const {
    data: ruleset,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ruleset', id],
    queryFn: async () => {
      const response = await axios.get(`${config.backend_base_url}api/public/ruleset/${id}`);
      return response.data as Ruleset;
    },
    enabled: !!id,
  });

  if (!id) {
    return <div>Invalid ruleset ID</div>;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !ruleset) {
    return <div>Ruleset not found</div>;
  }

  return (
    <div className={classes.mainContainer}>
      <div className={classes.container}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.centeredHeader}>{t('core.rulesetDetails')}</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12}>
                <div className={classes.centeredContent}>
                  <div style={{ marginBottom: '20px' }}>
                    <h3>{ruleset.name}</h3>
                    {ruleset.published && (
                      <span
                        style={{
                          background: '#4caf50',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      >
                        {t('core.published')}
                      </span>
                    )}
                  </div>

                  {ruleset.description && (
                    <div className={classes.section}>
                      <h5>{t('core.description')}</h5>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{ruleset.description}</p>
                    </div>
                  )}

                  {ruleset.team && (
                    <div className={classes.section}>
                      <h5>{t('core.associatedTeam')}</h5>
                      <div className={classes.teamCard}>
                        {ruleset.team.logo_url && (
                          <img
                            src={ruleset.team.logo_url}
                            alt={ruleset.team.name}
                            className={classes.teamLogo}
                          />
                        )}
                        <div>
                          <h6 style={{ margin: '0' }}>
                            <Link
                              to={`/guild/${ruleset.team.id}`}
                              style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                              {ruleset.team.name}
                            </Link>
                          </h6>
                          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                            @{ruleset.team.slug}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
                    <div>
                      {t('core.created')}: {new Date(ruleset.created_at).toLocaleDateString()}
                    </div>
                    <div>
                      {t('core.updated')}: {new Date(ruleset.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </div>
      <BubbleContainer pageKey="ruleset" helpText={t('help.ruleset')} />
    </div>
  );
}
